import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { CalendarClock, Trash2, Youtube, Instagram, Music2, Loader2 } from "lucide-react";
import { getSchedule, deleteSchedule, getBestTimes } from "../lib/api";
import { toast } from "sonner";

const PLAT = {
  tiktok: { label: "TikTok", icon: Music2, accent: "#25F4EE" },
  reels: { label: "Instagram Reels", icon: Instagram, accent: "#FF3EA5" },
  shorts: { label: "YouTube Shorts", icon: Youtube, accent: "#E83F3F" },
};

export default function SchedulerView() {
  const [items, setItems] = useState([]);
  const [times, setTimes] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([getSchedule(), getBestTimes()]);
      setItems(s); setTimes(t);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await deleteSchedule(id);
      toast.success("Removed from schedule");
    } catch {
      toast.error("Could not remove");
    }
    load();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="scheduler-view" className="mx-auto max-w-4xl space-y-8">
      <div className="border-b border-border pb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">Content Calendar</p>
        <h2 className="mt-1 flex items-center gap-3 font-display text-4xl tracking-wide text-white sm:text-5xl">
          <CalendarClock className="h-8 w-8 text-accent" /> POST SCHEDULE
        </h2>
        <p className="mt-2 max-w-xl font-body text-sm text-muted-foreground">
          Your posting plan. Add clips from any Publish panel — this is a planning reminder, not auto-posting.
        </p>
      </div>

      {/* Best-time cheatsheet */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {Object.entries(PLAT).map(([id, p]) => {
          const Icon = p.icon;
          return (
            <div key={id} className="border border-border bg-card p-4">
              <div className="flex items-center gap-2" style={{ color: p.accent }}>
                <Icon className="h-4 w-4" /> <span className="text-sm font-semibold text-white">{p.label}</span>
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best times</p>
              <div className="mt-1 space-y-0.5">
                {(times[id] || []).map((t, i) => <p key={i} className="font-mono text-xs text-accent">{t}</p>)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scheduled items */}
      <div>
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Upcoming ({items.length})</p>
        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <p className="border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
            Nothing scheduled yet. Open any clip's <span className="text-accent">Publish</span> panel and tap the calendar icon.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const p = PLAT[it.platform] || { label: it.platform, icon: CalendarClock, accent: "#CCFF00" };
              const Icon = p.icon;
              return (
                <div key={it.id} data-testid={`schedule-item-${it.id}`} className="flex items-center gap-4 border border-border bg-card p-4">
                  <div className="grid h-10 w-10 shrink-0 place-items-center border border-border" style={{ color: p.accent }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{it.clip_title}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">
                      {p.label} · <span className="text-accent">{it.scheduled_at}</span>
                    </p>
                  </div>
                  <button
                    data-testid={`schedule-remove-${it.id}`}
                    onClick={() => remove(it.id)}
                    className="text-muted-foreground transition-colors hover:text-primary"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}
